import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtPHP, MIN_LOAN, MAX_LOAN, TERMS, computeLoan } from "@/lib/api";
import { PCard } from "@/components/portal/PortalBits";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PortalApply() {
  const [amount, setAmount] = useState(100000);
  const [months, setMonths] = useState(12);
  const [purpose, setPurpose] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const calc = useMemo(() => computeLoan(amount, months), [amount, months]);
  const valid = amount >= MIN_LOAN && amount <= MAX_LOAN;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/portal/loans", { principal: amount, term_months: months, purpose: purpose || null });
      toast.success("Application submitted", { description: "Status: Pending admin review" });
      navigate(`/portal/loans/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="portal-apply-page" className="space-y-4">
      <h1 className="font-display text-2xl font-bold text-slate-900">Apply for a loan</h1>
      <PCard>
        <div className="text-xs text-slate-500">Loan amount</div>
        <Input type="number" value={amount} min={MIN_LOAN} max={MAX_LOAN} step={1000}
          onChange={(e) => setAmount(+e.target.value || 0)} data-testid="apply-amount-input"
          className="mt-1 h-12 text-xl font-bold text-slate-900 bg-slate-50 border-slate-200" />
        {!valid && <div className="text-xs text-rose-600 mt-1" data-testid="apply-amount-error">Amount must be ₱50,000 – ₱3,000,000</div>}
        <Slider className="mt-4" value={[Math.min(MAX_LOAN, Math.max(MIN_LOAN, amount))]} min={MIN_LOAN} max={MAX_LOAN} step={10000}
          onValueChange={(v) => setAmount(v[0])} data-testid="apply-amount-slider" />
        <div className="flex justify-between text-[11px] text-slate-400 mt-2"><span>₱50,000</span><span>₱3,000,000</span></div>
        <div className="text-xs text-slate-500 mt-5 mb-2">Term</div>
        <div className="grid grid-cols-4 gap-2">
          {TERMS.map((t) => (
            <button key={t} onClick={() => setMonths(t)} data-testid={`apply-term-${t}`}
              className={`py-2.5 rounded-xl text-sm font-semibold border transition-colors ${months === t ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>
              {t} mo
            </button>
          ))}
        </div>
      </PCard>
      <PCard className="space-y-2 text-sm" data-testid="apply-summary">
        {[
          ["Monthly interest (0.3%)", calc.monthly_interest],
          ["Total interest", calc.total_interest],
          ["Total repayment", calc.total_repayment],
        ].map(([k, v]) => (
          <div key={k} className="flex justify-between"><span className="text-slate-500">{k}</span><span className="font-semibold text-slate-900">{fmtPHP(v)}</span></div>
        ))}
        <div className="flex justify-between pt-2 border-t border-slate-100">
          <span className="text-slate-700 font-semibold">Monthly repayment</span>
          <span className="font-display text-lg font-bold text-blue-700" data-testid="apply-monthly">{fmtPHP(calc.monthly_payment)}</span>
        </div>
      </PCard>
      <Textarea placeholder="Purpose (optional)" value={purpose} onChange={(e) => setPurpose(e.target.value)}
        data-testid="apply-purpose-input" className="bg-white border-slate-200 text-slate-900" />
      <Button onClick={submit} disabled={!valid || busy} data-testid="apply-submit-button"
        className="w-full h-12 rounded-xl bg-[#0F4C81] hover:bg-blue-700 text-white font-semibold">
        {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Submit application
      </Button>
    </div>
  );
}
